import { assert, deepCloneWith, isRecord, value } from '@takeshape/util';
import omit from 'lodash/omit.js';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
const title = 'Klaviyo';
export const klaviyoSpec = 'https://raw.githubusercontent.com/klaviyo/openapi/01ba9844c118dd4e9218de91dd2391c0eaa688ba/openapi/stable.json';
export const klaviyoRevision = '2024-07-15';
const serviceProvider = {
    name: 'klaviyo',
    title,
    description: 'Turn your ecommerce data into revenue with personalized multi-channel marketing campaigns.',
    categories: ['ecommerce', 'marketing'],
    links: [
        { title: 'Homepage', url: 'https://www.klaviyo.com/' },
        { title: 'Docs', url: '/docs/services/providers/klaviyo' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('klaviyo'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'Klaviyo'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'klaviyo'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'Klaviyo'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    token: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'To find your API key for Klaviyo, log in to your Klaviyo account then access Account → Settings → API Keys'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            id: formData.id,
            title: formData.title ?? title,
            provider: 'klaviyo',
            serviceType: 'openapi',
            authenticationType: 'bearer',
            namespace: formData.namespace ?? title,
            options: {
                endpoint: 'https://a.klaviyo.com/',
                spec: klaviyoSpec,
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION,
                headers: {
                    revision: klaviyoRevision
                }
            }
        };
        return updateAuthentication(config, formData, {
            type: 'bearer',
            header: 'Authorization',
            prefix: 'Klaviyo-API-Key'
        });
    },
    specTransform,
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec', 'options.headers.revision']),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/lists')
    })
};
function isSchemaObject(object) {
    return isRecord(object) && object.$ref === undefined;
}
function isReferenceObject(object) {
    return isRecord(object) && object.$ref !== undefined;
}
export function specTransform(spec) {
    assert('openapi' in spec, 'Klaviyo uses an OpenAPI v3 spec makes sure you are using the correct url');
    return removeRevisionParameters(inlineSingleItemEnums(spec));
}
/**
 * Klaviyo uses hundreds of single item enums which make our GraphQL API a mess so we inline them
 */
function inlineSingleItemEnums(doc) {
    if (!doc.components?.schemas) {
        return doc;
    }
    const { schemas } = doc.components;
    const singleItemEnums = {};
    // Identify single-item enum schemas and store them for replacement
    for (const [name, schema] of Object.entries(schemas)) {
        if (isSchemaObject(schema) && schema.enum && schema.enum.length === 1) {
            singleItemEnums[name] = schema.enum[0]; // Store the single enum value
        }
    }
    const namesToOmit = Object.keys(singleItemEnums);
    if (!namesToOmit) {
        return doc;
    }
    const updatedSchemas = omit(schemas, namesToOmit);
    return deepCloneWith({ ...doc, components: { ...doc.components, schemas: updatedSchemas } }, (value) => {
        if (isReferenceObject(value)) {
            const { $ref, ...rest } = value;
            const refName = $ref.replace('#/components/schemas/', '');
            if (singleItemEnums[refName]) {
                const replacement = { ...rest, enum: [singleItemEnums[refName]] };
                return replacement;
            }
        }
    });
}
/**
 * Remove revision parameters because we set the header at the service level
 */
function removeRevisionParameters(spec) {
    return deepCloneWith(spec, (value, key, parent, clone) => {
        if (key === 'parameters' && Array.isArray(value)) {
            return value
                .filter((item) => !(isRecord(item) && item.name === 'revision'))
                .map((item, i) => clone(item, i, value));
        }
    });
}
export default serviceProvider;
